import { useEffect, useRef, useState } from "react"

function parseToolOutput(output) {
    if (!output) return null
    if (typeof output === "string") {
        try {
            return JSON.parse(output)
        } catch {
            return null
        }
    }
    return output
}

function extractToolSessions(message) {
    if (!message?.parts) return []
    const sessions = []
    for (const part of message.parts) {
        if (part.type?.startsWith("tool-") && part.state === "output-available") {
            const parsed = parseToolOutput(part.output)
            if (!parsed?.sessions) continue
            for (const s of parsed.sessions) {
                sessions.push({
                    id: s.event_id ?? s.id,
                    title: s.title,
                    meta: s.meta || "",
                    meta2: s.parent_session || s.breadcrumb || s.session_type || null,
                    saved: false,
                    calAdded: false,
                })
            }
        }
    }
    return sessions
}

export default function useToolSessions({ messages, resetKey }) {
    const [sessions, setSessions] = useState([])
    const [history, setHistory] = useState([])
    const [historyIndex, setHistoryIndex] = useState(-1)
    const turnHistoryIdRef = useRef(null)

    useEffect(() => {
        if (resetKey === 0) return
        setSessions([])
        setHistory([])
        setHistoryIndex(-1)
        turnHistoryIdRef.current = null
    }, [resetKey])

    const toggleSave = id => {
        setSessions(prev => prev.map(s => (s.id === id ? { ...s, saved: !s.saved } : s)))
    }

    const toggleCal = id => {
        setSessions(prev => prev.map(s => (s.id === id ? { ...s, calAdded: !s.calAdded } : s)))
    }

    const saveAll = ids => {
        const idSet = new Set(ids)
        setSessions(prev => prev.map(s => (idSet.has(s.id) ? { ...s, saved: true } : s)))
    }

    const addToHistory = newSessions => {
        setSessions(prev => {
            const existingIds = new Set(prev.map(s => s.id))
            const toAdd = newSessions.filter(s => !existingIds.has(s.id))
            return toAdd.length > 0 ? [...prev, ...toAdd] : prev
        })
        const ids = newSessions.map(s => s.id)
        setHistory(prev => [...prev, ids].slice(-20))
        setHistoryIndex(prev => prev + 1)
    }

    const replaceLatestHistory = newSessions => {
        setSessions(prev => {
            const existingIds = new Set(prev.map(s => s.id))
            const toAdd = newSessions.filter(s => !existingIds.has(s.id))
            return toAdd.length > 0 ? [...prev, ...toAdd] : prev
        })
        const ids = newSessions.map(s => s.id)
        setHistory(prev => {
            if (prev.length === 0) return prev
            const updated = [...prev]
            updated[updated.length - 1] = ids
            return updated
        })
    }

    const currentSessions =
        historyIndex >= 0
            ? (history[historyIndex] || []).map(id => sessions.find(s => s.id === id)).filter(Boolean)
            : []

    useEffect(() => {
        const lastAssistant = [...messages].reverse().find(m => m.role === "assistant")
        if (!lastAssistant) return

        const matchedSessions = extractToolSessions(lastAssistant)
        if (matchedSessions.length === 0) return

        if (turnHistoryIdRef.current !== lastAssistant.id) {
            turnHistoryIdRef.current = lastAssistant.id
            addToHistory(matchedSessions)
        } else {
            replaceLatestHistory(matchedSessions)
        }
    }, [messages])

    return {
        sessions,
        currentSessions,
        history,
        historyIndex,
        setHistoryIndex,
        toggleSave,
        toggleCal,
        saveAll,
    }
}
